import { NOME_PODER_PROCURACAO, PODERES_PROCURACAO } from '@social/shared';
import type { PoderProcuracao } from '@social/shared';

/**
 * Os quatro atos que a tela oferece, e o que cada um precisa saber.
 *
 * Uma tela só com os campos trocando, e não quatro telas: o contador chega
 * sabendo qual ato quer, e a escolha do tipo é a primeira pergunta. Quatro
 * rotas separadas o obrigariam a acertar o caminho antes de ver as opções —
 * e "qual das quatro telas é a de distrato?" é uma pergunta que a interface
 * não deveria fazer.
 */

export const ATOS_ESPECIAIS = ['DISTRATO', 'PROCURACAO', 'ATA', 'TRANSFORMACAO'] as const;
export type AtoEspecial = (typeof ATOS_ESPECIAIS)[number];

export const NOME_ATO: Record<AtoEspecial, string> = {
  DISTRATO: 'Distrato social',
  PROCURACAO: 'Procuração',
  ATA: 'Ata de reunião',
  TRANSFORMACAO: 'Transformação de tipo',
};

/**
 * O que cada ato FAZ, em uma linha.
 *
 * Aparece junto do seletor porque a diferença entre "alteração" e
 * "transformação" não é óbvia para quem não vive de direito societário — e
 * escolher o ato errado só se descobre quando a Junta devolve.
 */
export const O_QUE_FAZ: Record<AtoEspecial, string> = {
  DISTRATO: 'Dissolve e extingue a sociedade. Depois dele não há mais atos.',
  PROCURACAO: 'A sociedade nomeia um procurador. Não altera o contrato.',
  ATA: 'Registra uma reunião de sócios e o que foi deliberado.',
  TRANSFORMACAO:
    'Muda o TIPO da sociedade sem dissolvê-la — o CNPJ e os contratos continuam.',
};

/** A rota de cada ato. Uma por ato, para cada uma validar com o schema dela. */
export const CAMINHO: Record<AtoEspecial, string> = {
  DISTRATO: 'distrato',
  PROCURACAO: 'procuracao',
  ATA: 'ata',
  TRANSFORMACAO: 'transformacao',
};

export const OPCOES_PODERES = PODERES_PROCURACAO.map((p) => ({
  valor: p as PoderProcuracao,
  rotulo: NOME_PODER_PROCURACAO[p],
}));

/** Estado do formulário — texto, porque é o que está na tela. */
export interface RascunhoAto {
  tipo: AtoEspecial;
  data: string;

  // Distrato
  motivo: string;
  responsavelLivrosId: string;
  acervo: string;
  temPassivo: boolean;

  // Procuração
  outorgadoId: string;
  poderes: PoderProcuracao[];
  validaAte: string;
  podeSubstabelecer: boolean;
  finalidade: string;

  // Ata
  hora: string;
  local: string;
  presentesIds: string[];
  ordemDoDia: string;
  deliberacoes: string;
  presidenteId: string;
  secretarioId: string;

  // Transformação
  tipoNovo: string;
}

export function rascunhoVazio(hoje: string): RascunhoAto {
  return {
    tipo: 'DISTRATO',
    data: hoje,
    motivo: '',
    responsavelLivrosId: '',
    acervo: '0,00',
    temPassivo: false,
    outorgadoId: '',
    poderes: [],
    validaAte: '',
    podeSubstabelecer: false,
    finalidade: '',
    hora: '10:00',
    local: 'sede social',
    presentesIds: [],
    ordemDoDia: '',
    deliberacoes: '',
    presidenteId: '',
    secretarioId: '',
    tipoNovo: '',
  };
}

/**
 * Uma linha por item, ignorando as vazias.
 *
 * A pauta e as deliberações são digitadas num `textarea`, uma por linha — é
 * como o contador as escreve. Linha vazia no meio é gesto normal de quem
 * organiza o texto, e mandá-la produziria um item em branco na ata.
 */
export function linhas(texto: string): string[] {
  return texto
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/** "1.234,56" → 123456. Ver `centavosDeReais` em `sociedade-nova-comum`. */
export function centavos(texto: string): number {
  const limpo = texto.replace(/\./g, '').replace(',', '.');
  const n = Number(limpo);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/**
 * O corpo que a API espera, conforme o tipo.
 *
 * Cada ato tem uma rota própria com o schema dele, então o corpo é montado
 * aqui já no formato daquele schema — e não numa união que o servidor teria
 * de destrinchar.
 */
export function corpoDoAto(r: RascunhoAto): Record<string, unknown> {
  if (r.tipo === 'DISTRATO') {
    return {
      data: r.data,
      motivo: r.motivo,
      responsavelLivrosId: r.responsavelLivrosId,
      acervoLiquidoCentavos: centavos(r.acervo),
      temPassivo: r.temPassivo,
    };
  }

  if (r.tipo === 'PROCURACAO') {
    return {
      data: r.data,
      outorgadoId: r.outorgadoId,
      poderes: r.poderes,
      // Vazio é prazo INDETERMINADO (`null`), não uma data em branco.
      validaAte: r.validaAte || null,
      podeSubstabelecer: r.podeSubstabelecer,
      ...(r.finalidade.trim() ? { finalidade: r.finalidade.trim() } : {}),
    };
  }

  if (r.tipo === 'ATA') {
    return {
      data: r.data,
      hora: r.hora,
      local: r.local,
      presentesIds: r.presentesIds,
      ordemDoDia: linhas(r.ordemDoDia),
      deliberacoes: linhas(r.deliberacoes),
      presidenteId: r.presidenteId,
      secretarioId: r.secretarioId,
    };
  }

  return {
    data: r.data,
    tipoNovo: r.tipoNovo,
    // O schema aceita só `true`: sem unanimidade o rito é outro, com direito
    // de retirada, que este produto ainda não monta. A tela obriga a marcar.
    consentimentoUnanime: true,
    motivo: r.motivo,
  };
}

/**
 * O que ainda falta preencher — ou `null` quando está pronto.
 *
 * Devolve a MENSAGEM, e não um booleano: o botão desabilitado sem dizer o que
 * falta é o pior estado de um formulário longo, porque a pessoa relê tudo
 * procurando o campo.
 */
export function oQueFalta(r: RascunhoAto): string | null {
  if (!r.data) return 'Informe a data do instrumento.';

  if (r.tipo === 'DISTRATO') {
    if (r.motivo.trim().length < 10) return 'Descreva o motivo da dissolução.';
    if (!r.responsavelLivrosId) return 'Escolha quem fica responsável pelos livros.';
    return null;
  }

  if (r.tipo === 'PROCURACAO') {
    if (!r.outorgadoId) return 'Escolha quem recebe os poderes.';
    if (r.poderes.length === 0) return 'Marque ao menos um poder.';
    return null;
  }

  if (r.tipo === 'ATA') {
    if (!r.hora) return 'Informe a hora da reunião.';
    if (r.presentesIds.length === 0) return 'Marque quem esteve presente.';
    if (linhas(r.ordemDoDia).length === 0) return 'Escreva ao menos um item da pauta.';
    if (linhas(r.deliberacoes).length === 0) return 'Escreva ao menos uma deliberação.';
    if (!r.presidenteId || !r.secretarioId) return 'Escolha o presidente e o secretário.';
    return null;
  }

  if (!r.tipoNovo) return 'Escolha o novo tipo societário.';
  if (r.motivo.trim().length < 10) return 'Descreva o motivo da transformação.';
  return null;
}
